/**
 * Transaction Type Configuration for Expense Ledger
 */

export enum TransactionType {
  // Unit Transactions
  INCOME_CONTRIBUTION = 'incomeContribution',
  GROWTH_CONTRIBUTION = 'growthContribution',
  INCOME_UNIT_SELL = 'incomeUnitSell',
  GROWTH_UNIT_SELL = 'growthUnitSell',

  // Asset Purchases
  INCOME_ASSET_PURCHASE = 'incomeAssetPurchase',
  GROWTH_ASSET_PURCHASE = 'growthAssetPurchase',

  // Asset Revaluations
  INCOME_REVALUATION = 'incomeRevaluation',
  GROWTH_REVALUATION = 'growthRevaluation',

  // Asset Sales
  INCOME_ASSET_SELL = 'incomeAssetSell',
  GROWTH_ASSET_SELL = 'growthAssetSell',

  // Operational Expenses
  CASH_USED = 'cashUsed'
}

export interface TransactionTypeConfig {
  label: string;
  group: TransactionGroup;
  color: string;
  backgroundColor: string;
  icon: string;
  description: string;
}

export type TransactionGroup = 'unitTransactions' | 'assetPurchases' | 'assetRevaluations' | 'assetSales' | 'expenses';

export const TRANSACTION_TYPE_CONFIG: Record<TransactionType, TransactionTypeConfig> = {
  // Unit Transactions
  [TransactionType.INCOME_CONTRIBUTION]: {
    label: 'Buy Income Units',
    group: 'unitTransactions',
    color: '#10b981',
    backgroundColor: '#d1fae5',
    icon: '',
    description: 'Investor purchases income units'
  },
  [TransactionType.GROWTH_CONTRIBUTION]: {
    label: 'Buy Growth Units',
    group: 'unitTransactions',
    color: '#10b981',
    backgroundColor: '#d1fae5',
    icon: '',
    description: 'Investor purchases growth units'
  },
  [TransactionType.INCOME_UNIT_SELL]: {
    label: 'Sell Income Units',
    group: 'unitTransactions',
    color: '#10b981',
    backgroundColor: '#d1fae5',
    icon: '',
    description: 'Investor sells income units'
  },
  [TransactionType.GROWTH_UNIT_SELL]: {
    label: 'Sell Growth Units',
    group: 'unitTransactions',
    color: '#10b981',
    backgroundColor: '#d1fae5',
    icon: '',
    description: 'Investor sells growth units'
  },

  // Asset Purchases
  [TransactionType.INCOME_ASSET_PURCHASE]: {
    label: 'Buy Income Asset',
    group: 'assetPurchases',
    color: '#3b82f6',
    backgroundColor: '#dbeafe',
    icon: '',
    description: 'Asset purchased for income fund'
  },
  [TransactionType.GROWTH_ASSET_PURCHASE]: {
    label: 'Buy Growth Asset',
    group: 'assetPurchases',
    color: '#3b82f6',
    backgroundColor: '#dbeafe',
    icon: '',
    description: 'Asset purchased for growth fund'
  },

  // Asset Revaluations
  [TransactionType.INCOME_REVALUATION]: {
    label: 'Income Revaluation',
    group: 'assetRevaluations',
    color: '#a855f7',
    backgroundColor: '#f3e8ff',
    icon: '',
    description: 'Income fund asset revalued'
  },
  [TransactionType.GROWTH_REVALUATION]: {
    label: 'Growth Revaluation',
    group: 'assetRevaluations',
    color: '#a855f7',
    backgroundColor: '#f3e8ff',
    icon: '',
    description: 'Growth fund asset revalued'
  },

  // Asset Sales
  [TransactionType.INCOME_ASSET_SELL]: {
    label: 'Sell Income Asset',
    group: 'assetSales',
    color: '#ef4444',
    backgroundColor: '#fee2e2',
    icon: '',
    description: 'Income fund asset sold'
  },
  [TransactionType.GROWTH_ASSET_SELL]: {
    label: 'Sell Growth Asset',
    group: 'assetSales',
    color: '#ef4444',
    backgroundColor: '#fee2e2',
    icon: '',
    description: 'Growth fund asset sold'
  },

  // Operational Expenses
  [TransactionType.CASH_USED]: {
    label: 'Operational Expense',
    group: 'expenses',
    color: '#f59e0b',
    backgroundColor: '#fef3c7',
    icon: '',
    description: 'Monthly operational expenses and fees'
  }
};

export const TRANSACTION_GROUPS = {
  unitTransactions: {
    label: 'Unit Transactions',
    types: [
      TransactionType.INCOME_CONTRIBUTION,
      TransactionType.GROWTH_CONTRIBUTION,
      TransactionType.INCOME_UNIT_SELL,
      TransactionType.GROWTH_UNIT_SELL
    ]
  },
  assetPurchases: {
    label: 'Asset Purchases',
    types: [
      TransactionType.INCOME_ASSET_PURCHASE,
      TransactionType.GROWTH_ASSET_PURCHASE
    ]
  },
  assetRevaluations: {
    label: 'Asset Revaluations',
    types: [
      TransactionType.INCOME_REVALUATION,
      TransactionType.GROWTH_REVALUATION
    ]
  },
  assetSales: {
    label: 'Asset Sales',
    types: [
      TransactionType.INCOME_ASSET_SELL,
      TransactionType.GROWTH_ASSET_SELL
    ]
  },
  expenses: {
    label: 'Operational Expenses',
    types: [TransactionType.CASH_USED]
  }
};

export const ALL_TRANSACTION_TYPES = Object.values(TransactionType);

// Mapping for legacy/alternate transaction type names
const TRANSACTION_TYPE_ALIASES: Record<string, TransactionType> = {
  'growthUnits': TransactionType.GROWTH_CONTRIBUTION,
  'incomeUnits': TransactionType.INCOME_CONTRIBUTION,
  'growthAssetRevaluation': TransactionType.GROWTH_REVALUATION,
  'incomeAssetRevaluation': TransactionType.INCOME_REVALUATION,
  'growth': TransactionType.GROWTH_CONTRIBUTION,
  'income': TransactionType.INCOME_CONTRIBUTION
};

// Normalize transaction type names to match standard config
export const normalizeTransactionType = (type: string): string => {
  if (!type) return type;
  
  // Check if it's an alias
  if (type in TRANSACTION_TYPE_ALIASES) {
    return TRANSACTION_TYPE_ALIASES[type];
  }
  
  // If already a valid type, return as-is
  if (type in TRANSACTION_TYPE_CONFIG) {
    return type;
  }
  
  // Return original if no mapping found
  return type;
};

export const getTransactionTypeLabel = (type: string): string => {
  const normalizedType = normalizeTransactionType(type);
  const config = TRANSACTION_TYPE_CONFIG[normalizedType as TransactionType];
  return config ? config.label : type;
};

export const getTransactionTypeConfig = (type: string): TransactionTypeConfig | undefined => {
  const normalizedType = normalizeTransactionType(type);
  return TRANSACTION_TYPE_CONFIG[normalizedType as TransactionType];
};
